import { claudeCodeMcpCommand } from '@fos/shared/contracts/mcp';
import type { ApiTokenDto } from '@fos/shared/contracts/tokens';
import {
  API_TOKEN_ACCESS_LABELS,
  API_TOKEN_LOCAL_RUNNER_SHORT,
  tokenExpiryLabel,
  tokenScopeLabel,
} from '@fos/shared/labels';
import { formatDate, formatRelativePast } from '../../../lib/format';

/** Ważność w formularzu „Nowy token”: 30 / 90 dni, rok albo własna data (do roku). Domyślnie 90 dni. */
export const EXPIRY_CHOICES = ['30', '90', '365', 'custom'] as const;
export type ExpiryChoice = (typeof EXPIRY_CHOICES)[number];
export const DEFAULT_EXPIRY: ExpiryChoice = '90';

export function expiryChoiceLabel(choice: ExpiryChoice): string {
  return choice === 'custom' ? 'Własna data' : tokenExpiryLabel(Number(choice));
}

/** Adres serwera MCP: ten sam host co aplikacja (lokalnie Vite przekazuje `/mcp` do API). */
export function mcpBaseUrl(): string {
  return typeof window === 'undefined' ? 'http://localhost:5180' : window.location.origin;
}

export function connectCommand(token?: string): string {
  return claudeCodeMcpCommand(mcpBaseUrl(), token);
}

/**
 * Linia opisu w liście: „fos_pat_ab12… · Tylko odczyt · REA Nieruchomości” (+ „zleca pracę na komputerze”, gdy token
 * ma tę zgodę — Fala 6).
 */
export function tokenSummary(
  t: Pick<ApiTokenDto, 'prefix' | 'access' | 'scope'> & { localRunnerAllowed?: boolean; kind?: ApiTokenDto['kind'] },
): string {
  // konektor (OAuth): zamiast prefiksu — rodzaj (tokeny klienta zmieniają się co godzinę)
  const head = t.kind === 'oauth' ? 'Konektor' : `${t.prefix}…`;
  const base = `${head} · ${API_TOKEN_ACCESS_LABELS[t.access]} · ${tokenScopeLabel(t.scope)}`;
  return t.localRunnerAllowed ? `${base} · ${API_TOKEN_LOCAL_RUNNER_SHORT}` : base;
}

/** „użyty dziś, 9:42” / „jeszcze nieużywany”. */
export function lastUsedText(t: Pick<ApiTokenDto, 'lastUsedAt'>, now: Date = new Date()): string {
  return t.lastUsedAt ? `użyty ${formatRelativePast(t.lastUsedAt, now)}` : 'jeszcze nieużywany';
}

/** „wygasa 5.01.2027” / „wygasł 1.10.2026” / „bez daty wygaśnięcia”. */
export function expiryText(t: Pick<ApiTokenDto, 'expiresAt' | 'status'>): string {
  if (!t.expiresAt) return 'bez daty wygaśnięcia';
  return `${t.status === 'expired' ? 'wygasł' : 'wygasa'} ${formatDate(t.expiresAt)}`;
}

/** Odmiana: 1 token, 2 tokeny, 5 tokenów. */
export function tokensCount(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (n === 1) return '1 token';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${n} tokeny`;
  return `${n} tokenów`;
}
